/** biome-ignore-all lint/performance/noAccumulatingSpread: <explanation> */
import {
  defaultMobileOptions,
  defaultMobileStyles,
  defaultOptions,
  defaultStyles,
  draftOptions,
  translatableOptionKeys,
} from 'client/constants/editorSettings'
import i18next from 'client/i18n'
import defaultScheme from 'common/constants/defaultScheme'
import mapToolBoxToStructure, {
  getContainerEntityStructure,
  getMovableEntityStructure,
} from 'common/constants/mapToolBoxToStructure'
import structureTypes from 'common/constants/structureTypes'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { createCarousel } from 'src/shared/entities/carousel/create-carousel'
import { createColumn } from 'src/shared/entities/column/create-column'
import { createOfferPrice } from 'src/shared/entities/offer-price/create-offer-price'
import { createRow } from 'src/shared/entities/row/create-row'
import { createSection } from 'src/shared/entities/section/create-section'
import { createText } from 'src/shared/entities/text/create-text'
import {
  generateHtmlAttrId,
  generateId,
} from 'src/shared/entities/utils/generate-base-entity'

const getDefaultStyles = type =>
  defaultStyles[type] !== undefined ? defaultStyles[type] : {}

const getMobileDefaultStyles = type =>
  defaultMobileStyles[type] !== undefined ? defaultMobileStyles[type] : {}

const translateOptions = (locale, options) =>
  Object.keys(options).forEach(optionKey => {
    const t = i18next.getFixedT(locale)
    if (translatableOptionKeys.includes(optionKey)) {
      options[optionKey] = t(options[optionKey])
    }

    if (draftOptions.includes(optionKey)) {
      const draftEntity = JSON.parse(options[optionKey])
      draftEntity.blocks.forEach(block => {
        Object.keys(block).forEach(blockKey => {
          if (blockKey === 'text') {
            block[blockKey] = t(block[blockKey]) || block[blockKey]
          }
        })
      })

      options[optionKey] = JSON.stringify(draftEntity)
    }
  })

const getDefaultOptions = type =>
  defaultOptions[type] !== undefined ? defaultOptions[type] : {}

const getDefaultMobileOptions = type =>
  defaultMobileOptions[type] !== undefined ? defaultMobileOptions[type] : {}

const createDefaultComponent = (
  locale,
  type,
  parentId,
  styles,
  mobileStyles,
  options,
  mobileOptions,
  masterBlockId,
) => {
  // do not mutate default options
  const calculatedOptions = {
    ...getDefaultOptions(type),
    ...options,
    attrId: generateHtmlAttrId(type),
  }
  const calculatedMobileOptions = {
    ...getDefaultMobileOptions(type),
    ...mobileOptions,
  }
  translateOptions(locale, calculatedOptions)
  translateOptions(locale, calculatedMobileOptions)

  const newEntity = {
    id: generateId(),
    type,
    styles: {
      ...getDefaultStyles(type),
      ...styles,
    },
    mobileStyles: {
      ...getMobileDefaultStyles(type),
      ...mobileStyles,
    },
    options: calculatedOptions,
    mobileOptions: calculatedMobileOptions,
  }

  if (masterBlockId) {
    newEntity.masterBlockId = masterBlockId
  }

  if (parentId) {
    newEntity.parentId = parentId
  }

  return newEntity
}

const createComplexComponent = (
  locale,
  type,
  parentId,
  styles,
  mobileStyles,
  options,
  mobileOptions,
  masterBlockId,
) => ({
  ...createDefaultComponent(
    locale,
    type,
    parentId,
    styles,
    mobileStyles,
    options,
    mobileOptions,
    masterBlockId,
  ),
  childIds: [],
})

const getColumnSize = type => {
  switch (type) {
    case structureTypes.COLUMN_12:
      return 12
    case structureTypes.COLUMN_8:
      return 8
    case structureTypes.COLUMN_6:
      return 6
    case structureTypes.COLUMN_4:
      return 4
    case structureTypes.COLUMN_3:
      return 3
    default:
      return 12
  }
}

export const createColumnEntity = (locale, type, parentId, masterBlockId) => {
  const column = createComplexComponent(
    locale,
    structureTypes.COLUMN,
    parentId,
    null,
    null,
    null,
    null,
    masterBlockId,
  )
  column.options.size = getColumnSize(type)

  return column
}

const createEntity = (
  locale,
  type,
  parentId,
  styles,
  mobileStyles,
  options,
  mobileOptions,
  masterBlockId = null,
) => {
  switch (type) {
    case EntityTypeEnum.Text:
      return createText(parentId, masterBlockId)
    case EntityTypeEnum.Body:
    case EntityTypeEnum.BlogPostBody:
    case EntityTypeEnum.BlogPageBody:
    case EntityTypeEnum.BlogPostLayoutBody:
    case EntityTypeEnum.WebsiteBlogPostBody:
    case EntityTypeEnum.WebsiteBlogPageBody:
    case EntityTypeEnum.WebsiteBlogPostLayoutBody:
    case EntityTypeEnum.ContentBox:
    case EntityTypeEnum.Popup:
    case EntityTypeEnum.RemotePopupBody:
    case EntityTypeEnum.InlineBody:
    case structureTypes.SECTION:
    case structureTypes.ROW:
    case structureTypes.COLUMN:
      return createComplexComponent(
        locale,
        type,
        parentId,
        styles,
        mobileStyles,
        options,
        mobileOptions,
        masterBlockId,
      )
    case EntityTypeEnum.Section:
      return createSection(parentId, masterBlockId)
    case structureTypes.COLUMN_12:
    case structureTypes.COLUMN_8:
    case structureTypes.COLUMN_6:
    case structureTypes.COLUMN_4:
    case structureTypes.COLUMN_3:
      return createColumnEntity(locale, type, parentId, masterBlockId)
    case EntityTypeEnum.Carousel:
      return createCarousel(parentId, masterBlockId)
    case EntityTypeEnum.Row:
      return createRow(parentId, masterBlockId, options)
    case EntityTypeEnum.Column:
      return createColumn({ parentId, masterBlockId, options })
    case EntityTypeEnum.OfferPrice:
      return createOfferPrice(parentId, masterBlockId)
    default:
      return createDefaultComponent(
        locale,
        type,
        parentId,
        styles,
        mobileStyles,
        options,
        mobileOptions,
        masterBlockId,
      )
  }
}

const insertChild = (childIds, id, position) => [
  ...childIds.slice(0, position),
  id,
  ...childIds.slice(position),
]

const buildStructureByScheme = (
  locale,
  scheme,
  result = {},
  parentId,
  initialParentId,
  masterBlockId = null,
) => {
  if (!scheme.children) {
    return result
  }

  return scheme.children.reduce(
    ({ structure: parentStructure = {} }, current, index, array) => {
      const currentType = Object.keys(current)[0]
      const schemeObject = current[currentType]
      const entity = createEntity(
        locale,
        currentType,
        parentId || initialParentId,
        schemeObject.styles,
        schemeObject.mobileStyles,
        schemeObject.options,
        schemeObject.mobileOptions,
        masterBlockId,
      )

      const newResult = {
        ascendantId: result.ascendantId || parentId || entity.id,
        structure: {
          ...(result ? result.structure : null),
          ...parentStructure,
          [entity.id]: entity,
        },
      }

      if (parentId) {
        let childIds
        if (parentStructure[parentId]) {
          childIds = [...parentStructure[parentId].childIds, entity.id]
        } else if (result.structure[parentId]) {
          childIds = [...result.structure[parentId].childIds, entity.id]
        } else {
          childIds = [entity.id]
        }

        newResult.structure[parentId] = {
          ...result.structure[parentId],
          childIds,
        }
      }

      return {
        ...newResult,
        ...(index + 1 <= array.length
          ? buildStructureByScheme(
              locale,
              current[currentType],
              newResult,
              entity.id,
              null,
              masterBlockId,
            )
          : null),
      }
    },
    {},
  )
}

const replaceChildScheme = ({ children }, addScheme) => {
  const addSchemeRootType = Object.keys(addScheme)[0]

  if (!children || children.length === 0) {
    return { children: [addScheme] }
  }

  return children.reduce((prev, child) => {
    const childType = Object.keys(child)[0]
    if (childType === addSchemeRootType) {
      return {
        ...prev,
        children: [addScheme],
      }
    }

    return {
      ...prev,
      children: [
        {
          [childType]: { ...replaceChildScheme(child[childType], addScheme) },
        },
      ],
    }
  }, {})
}

const createStructureNew = (
  locale,
  targetType,
  containerEntity = null,
  position = null,
  masterBlockId = null,
) => {
  const containerNeedScheme = containerEntity
    ? defaultScheme[containerEntity.type]
    : {}

  const targetScheme = mapToolBoxToStructure[targetType]
  const resultScheme = replaceChildScheme(containerNeedScheme, targetScheme)
  const { ascendantId, structure } = buildStructureByScheme(
    locale,
    resultScheme,
    {},
    null,
    containerEntity?.id,
    masterBlockId,
  )

  if (!containerEntity) {
    return { ascendantId, structure }
  }

  const parentEntity = {
    ...containerEntity,
    childIds: insertChild(containerEntity.childIds, ascendantId, position),
  }

  return {
    ascendantId,
    structure: {
      ...structure,
      [parentEntity.id]: parentEntity,
    },
  }
}

function replaceEntitiesIds(entities) {
  const oldNewIds = {}
  entities.forEach(entity => {
    oldNewIds[entity.id] = generateId()
  })
  // assume first is an ascendant
  return entities.reduce(
    (prev, entity) => [
      ...prev,
      {
        ...entity,
        id: oldNewIds[entity.id],
        parentId: oldNewIds[entity.parentId],
        ...(entity.childIds
          ? {
              childIds: entity.childIds
                .filter(id => Boolean(oldNewIds[id]))
                .map(id => oldNewIds[id]),
            }
          : {}),
      },
    ],
    [],
  )
}

function getAncestor(entities) {
  return entities.find((entity, index, array) => {
    const parent = array.find(e => e.id === entity.parentId)
    if (parent) {
      return false
    }

    return entity
  })
}

export const addBlockEntitiesTo = (
  blockEntities,
  containerEntity,
  locale,
  position = null,
  isMaster = false,
) => {
  const preparedBlockEntities = isMaster
    ? blockEntities
    : replaceEntitiesIds(blockEntities)
  const blockAncestor = getAncestor(preparedBlockEntities)
  const movableReverseStructure = getMovableEntityStructure(blockAncestor.type)
  const containerStructure = getContainerEntityStructure(containerEntity.type)
  const buildMap = movableReverseStructure.filter(value =>
    containerStructure.includes(value),
  )

  if (!buildMap.length) {
    return {
      [containerEntity.id]: {
        ...containerEntity,
        childIds: insertChild(
          containerEntity.childIds,
          blockAncestor.id,
          position,
        ),
      },
      ...preparedBlockEntities.reduce(
        (acc, entity) => ({
          ...acc,
          [entity.id]: {
            ...(entity.id === blockAncestor.id
              ? { ...entity, parentId: containerEntity.id }
              : entity),
          },
        }),
        {},
      ),
    }
  }
  const structure = {}
  let ancestorParentId = containerEntity.id

  buildMap.reduce((prevEntity, entityType, index, array) => {
    const entity = createEntity(
      locale,
      entityType,
      prevEntity?.id || ancestorParentId,
    )
    structure[entity.id] = entity
    if (prevEntity) {
      structure[prevEntity.id].childIds.push(entity.id)
    }

    // override parentId
    if (index + 1 === array.length) {
      ancestorParentId = entity.id
    }

    if (index === 0) {
      // set as a child of container entity
      structure[containerEntity.id] = {
        ...containerEntity,
        childIds: insertChild(containerEntity.childIds, entity.id, position),
      }
    }

    return entity
  }, null)

  structure[ancestorParentId].childIds.push(blockAncestor.id)

  return preparedBlockEntities.reduce(
    (acc, entity) => ({
      ...acc,
      [entity.id]: {
        ...(entity.id === blockAncestor.id
          ? { ...entity, parentId: ancestorParentId }
          : entity),
      },
    }),
    structure,
  )
}

export const createDestinationStructure = (
  locale,
  movableEntity,
  parentEntity,
  position,
  masterBlockId,
) => {
  const movableReverseStructure = getMovableEntityStructure(movableEntity.type)
  const containerStructure = getContainerEntityStructure(parentEntity.type)
  const buildMap = movableReverseStructure.filter(value =>
    containerStructure.includes(value),
  )

  const structure = {}
  let parentId = parentEntity.id
  let ascendantId = null
  let calculatedPosition = position

  buildMap.reduce((prevEntity, buildType, index, array) => {
    const entity = createEntity(locale, buildType, prevEntity?.id || parentId)
    if (masterBlockId) {
      entity.masterBlockId = masterBlockId
    }
    structure[entity.id] = entity
    if (prevEntity) {
      structure[prevEntity.id].childIds.push(entity.id)
    }

    if (index === 0) {
      ascendantId = entity.id
      calculatedPosition = 0
    }

    // override parentId
    if (index + 1 === array.length) {
      parentId = entity.id
    }

    return entity
  }, null)

  return { parentId, structure, ascendantId, calculatedPosition }
}

export {
  createStructureNew as default,
  createEntity,
  buildStructureByScheme,
  createStructureNew as createStructure,
}
